import React from "react";
import { Link } from "react-router-dom";
import { NOW, OBSERVATIONS, BUILDING, ARTIFACTS } from "@/data/content";
import { SectionHeader } from "@/components/PageShell";

// A restrained editorial "state of the work" module.
// Combines Now (currently building / studying / offering) with
// a Recently Added strip that reflects the latest entries in the archive.
export default function ArchiveNow() {
  const recentObs = OBSERVATIONS[0];
  const recentBuilding = BUILDING.find((b) => b.url) || BUILDING[0];
  const recentArtifact = ARTIFACTS[0];

  return (
    <section
      data-testid="home-archive-now"
      className="border-t border-ink-line bg-ink-base py-20 md:py-28"
    >
      <div className="container-edge">
        <SectionHeader
          eyebrow="The archive, currently"
          title="A working set."
          lead={`Updated ${NOW.updated}. The archive grows in three directions at once.`}
        />

        <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-px bg-ink-line border border-ink-line">
          <div className="bg-ink-base p-8 md:p-10" data-testid="now-building">
            <p className="eyebrow-gold mb-6">Now building</p>
            <ul className="space-y-3 text-paper-secondary text-base leading-relaxed">
              {NOW.building.map((line, i) => (
                <li key={i} className="flex gap-3">
                  <span className="text-gold shrink-0">·</span>
                  <span>{line}</span>
                </li>
              ))}
            </ul>
            <Link to="/building" data-testid="now-building-link" className="btn-arrow mt-8">
              <span>See the slate</span>
              <span className="arrow">→</span>
            </Link>
          </div>

          <div className="bg-ink-base p-8 md:p-10" data-testid="now-studying">
            <p className="eyebrow-gold mb-6">Now studying</p>
            <ul className="space-y-3 text-paper-secondary text-base leading-relaxed">
              {NOW.studying.map((line, i) => (
                <li key={i} className="flex gap-3">
                  <span className="text-gold shrink-0">·</span>
                  <span>{line}</span>
                </li>
              ))}
            </ul>
            <Link to="/thinking" data-testid="now-thinking-link" className="btn-arrow mt-8">
              <span>Read the observations</span>
              <span className="arrow">→</span>
            </Link>
          </div>

          <div className="bg-ink-base p-8 md:p-10" data-testid="now-offering">
            <p className="eyebrow-gold mb-6">Now offering</p>
            <ul className="space-y-3 text-paper-secondary text-base leading-relaxed">
              {NOW.offering.map((line, i) => (
                <li key={i} className="flex gap-3">
                  <span className="text-gold shrink-0">·</span>
                  <span>{line}</span>
                </li>
              ))}
            </ul>
            <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2">
              <Link to="/coaching" data-testid="now-coaching-link" className="btn-arrow">
                <span>Coaching</span>
                <span className="arrow">→</span>
              </Link>
              <Link to="/speaking" data-testid="now-speaking-link" className="btn-arrow">
                <span>Speaking</span>
                <span className="arrow">→</span>
              </Link>
            </div>
          </div>
        </div>

        <div className="mt-20">
          <div className="flex items-baseline justify-between mb-8">
            <p className="eyebrow">Recently added to the archive</p>
            <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-paper-mute">A quiet cadence</p>
          </div>
          <ul className="grid grid-cols-1 md:grid-cols-3 gap-px bg-ink-line border border-ink-line" data-testid="recently-added">
            {recentObs && (
              <li className="bg-ink-base">
                <Link to={`/thinking/${recentObs.slug}`} className="block p-8 group" data-testid="recent-observation">
                  <p className="eyebrow mb-3">Thinking</p>
                  <p className="font-serif text-xl text-paper-primary group-hover:text-gold-warm transition-colors leading-snug text-balance">
                    {recentObs.headline}
                  </p>
                </Link>
              </li>
            )}
            {recentBuilding && (
              <li className="bg-ink-base">
                <Link to={`/building/${recentBuilding.slug}`} className="block p-8 group" data-testid="recent-building">
                  <p className="eyebrow mb-3">Building · {recentBuilding.category}</p>
                  <p className="font-serif text-xl text-paper-primary group-hover:text-gold-warm transition-colors leading-snug text-balance">
                    {recentBuilding.name}
                  </p>
                  <p className="mt-3 text-paper-tertiary text-sm leading-relaxed">{recentBuilding.lead}</p>
                </Link>
              </li>
            )}
            {recentArtifact && (
              <li className="bg-ink-base">
                <Link to={`/artifacts/${recentArtifact.slug}`} className="block p-8 group" data-testid="recent-artifact">
                  <p className="eyebrow mb-3">Artifacts</p>
                  <p className="font-serif text-xl text-paper-primary group-hover:text-gold-warm transition-colors leading-snug text-balance">
                    {recentArtifact.title}
                  </p>
                </Link>
              </li>
            )}
          </ul>
        </div>
      </div>
    </section>
  );
}
